import { type Material, NO_CATEGORY, formatAmount } from '@/components/sklad/materialTypes';
import { parseQty } from '@/components/sklad/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useMemo, useState } from 'react';

const INPUT =
  'w-full text-sm text-text bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all';

/** Velikosti řadíme číselně („35" < „40" < „100"), jinak abecedně. */
function bySize(a: string, b: string): number {
  return a.localeCompare(b, 'cs', { numeric: true });
}

/**
 * Naskladnění po krocích: druh → barva → velikost → počet kusů. Kroky, které
 * u druhu nedávají smysl (např. materiál bez barev), se přeskočí. Režim
 * „nastavit stav" = inventura (skutečný počet místo přičtení).
 */
export function MaterialAddWizard({
  items,
  onSaved,
  onClose,
  onError,
}: {
  items: Material[];
  onSaved: (msg: string) => void;
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  useEscapeKey(onClose);
  const active = useMemo(() => items.filter((i) => !i.archived_at), [items]);
  const [category, setCategory] = useState<string | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [itemId, setItemId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState<'add' | 'set'>('add');
  const [saving, setSaving] = useState(false);

  const categories = useMemo(
    () =>
      [...new Set(active.map((i) => i.category || NO_CATEGORY))].sort((a, b) =>
        a.localeCompare(b, 'cs'),
      ),
    [active],
  );
  const inCategory = active.filter((i) => (i.category || NO_CATEGORY) === category);
  const colors = [...new Set(inCategory.map((i) => i.color))].sort((a, b) =>
    a.localeCompare(b, 'cs'),
  );
  // Materiál bez barev (jen jedna prázdná) krok barvy přeskočí.
  const needsColor = colors.length > 1 || (colors.length === 1 && colors[0] !== '');
  const hexOf = (c: string) =>
    inCategory.find((i) => i.color === c && i.color_hex)?.color_hex ?? '';
  const inColor = inCategory
    .filter((i) => !needsColor || i.color === color)
    .sort((a, b) => bySize(a.size, b.size) || a.name.localeCompare(b.name, 'cs'));
  const selected = active.find((i) => i.id === itemId) ?? null;

  function pickCategory(c: string) {
    setCategory(c);
    setColor(null);
    setItemId(null);
  }
  function pickColor(c: string) {
    setColor(c);
    setItemId(null);
  }

  async function save() {
    if (!selected) return;
    const n = parseQty(amount);
    if (!Number.isFinite(n) || n < 0 || (mode === 'add' && n === 0)) {
      onError(mode === 'add' ? 'Zadej, kolik kusů přidat' : 'Zadej skutečný stav');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'add') {
        await apiFetch(`/api/material/items/${selected.id}/add`, {
          method: 'POST',
          body: { amount: n },
        });
      } else {
        await apiFetch(`/api/material/items/${selected.id}/adjust`, {
          method: 'POST',
          body: { stock_qty: n, note: 'Inventura' },
        });
      }
      const label = [selected.category, selected.color, selected.size].filter(Boolean).join(' · ');
      onSaved(
        mode === 'add'
          ? `Naskladněno ${formatAmount(n, selected.unit)}: ${label}`
          : `Stav nastaven na ${formatAmount(n, selected.unit)}: ${label}`,
      );
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  const tile = (active: boolean) =>
    `rounded-lg border px-3 py-2 text-sm font-semibold cursor-pointer transition-colors ${
      active
        ? 'border-primary bg-primary-50 text-primary ring-2 ring-primary/30'
        : 'border-border bg-surface text-text hover:border-primary'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-xl my-10 bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text pb-4 mb-4 border-b border-border">
          Naskladnit drobný materiál
        </h2>

        <Step n={1} title="Druh">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => pickCategory(c)}
                className={tile(c === category)}
              >
                {c}
              </button>
            ))}
          </div>
        </Step>

        {category && needsColor && (
          <Step n={2} title="Barva">
            <div className="flex flex-wrap gap-2">
              {colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => pickColor(c)}
                  className={`${tile(c === color)} inline-flex items-center gap-2`}
                >
                  {hexOf(c) && (
                    <span
                      // Barva je data položky — Tailwind třídu pro libovolný hex nemá.
                      style={{ backgroundColor: hexOf(c) }}
                      className="inline-block h-4 w-4 rounded-full border border-border"
                    />
                  )}
                  {c || 'Bez barvy'}
                </button>
              ))}
            </div>
          </Step>
        )}

        {category && (!needsColor || color !== null) && (
          <Step n={needsColor ? 3 : 2} title="Velikost">
            <div className="flex flex-wrap gap-2">
              {inColor.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => setItemId(i.id)}
                  className={`${tile(i.id === itemId)} flex flex-col items-center min-w-[4.5rem]`}
                >
                  <span>{i.size || i.name}</span>
                  <span className="text-[11px] font-normal text-text-muted">
                    skladem {formatAmount(i.stock_qty, i.unit)}
                  </span>
                </button>
              ))}
            </div>
          </Step>
        )}

        {selected && (
          <Step n={needsColor ? 4 : 3} title="Počet">
            <div className="flex gap-1 mb-3 bg-bg rounded-lg p-1 w-fit">
              {(
                [
                  ['add', 'Přidat na sklad'],
                  ['set', 'Nastavit přesný stav'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMode(key)}
                  className={`text-sm font-semibold px-3 py-1.5 rounded-md cursor-pointer transition-colors ${
                    mode === key ? 'bg-surface text-primary shadow-card' : 'text-text-secondary'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 max-w-xs">
              <input
                // biome-ignore lint/a11y/noAutofocus: poslední krok průvodce, čeká se na počet
                autoFocus
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && save()}
                inputMode="decimal"
                placeholder={mode === 'add' ? 'Kolik přidat' : 'Kolik je skladem'}
                className={INPUT}
              />
              <span className="text-sm text-text-muted">{selected.unit}</span>
            </div>
            <p className="text-xs text-text-muted mt-2">
              Teď skladem {formatAmount(selected.stock_qty, selected.unit)}
              {selected.reserved > 0 &&
                ` · rezervováno pro výrobu ${formatAmount(selected.reserved, selected.unit)}`}
            </p>
          </Step>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !selected}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 cursor-pointer transition-colors disabled:opacity-50"
          >
            {saving ? 'Ukládám…' : mode === 'add' ? 'Naskladnit' : 'Nastavit stav'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="text-sm font-semibold text-text-secondary mb-2">
        <span className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded-full bg-primary text-white text-[11px]">
          {n}
        </span>
        {title}
      </div>
      {children}
    </div>
  );
}
